/**
 * ResetCredentialConfirmDialog
 *
 * Confirmation surface for the FI-admin credential-reset control on the
 * staff detail page. Composes the `@aaimplatform/ui` `Dialog`,
 * `Textarea`, and `Button` primitives with zero local re-implementations
 * per `.claude/rules/ui-package.md`; modelled on `SuspendConfirmDialog`.
 *
 * Framing: this is an operator control the FI admin exercises over their
 * own institution's staff access, not an end-user account-recovery
 * self-service flow. The copy states the three mechanics of the
 * operation plainly so the operator understands the blast radius before
 * confirming:
 *   1. the staff member's Aaim-managed password credential is deactivated
 *   2. a single-use reset invitation is emailed to them
 *   3. live sessions are revoked
 *
 * Validation: `reason` is required, minimum 10 characters, mirroring the
 * suspend / off-board / role-change reason contract. The parent
 * (`onConfirm`) owns the mutation and the step-up token plumbing.
 *
 * A failed reset renders inside the dialog (`errorMessage`, GH #2307): the
 * dialog stays open on failure, so an alert on the page behind it is
 * hidden by the modal.
 */

import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, Textarea } from '@aaimplatform/ui';

const MIN_REASON_LENGTH = 10;

export interface ResetCredentialConfirmDialogProps {
  /** Controls dialog visibility. Mirrors React Aria modal contract. */
  open: boolean;
  /** Visibility change callback (close button, Escape key, backdrop). */
  onOpenChange: (open: boolean) => void;
  /** Display name for the staff member whose credential is being reset. */
  staffName: string;
  /** Email the reset invitation will be delivered to. */
  staffEmail: string;
  /**
   * Submit handler. Receives the trimmed reason string. The parent owns
   * the network call (`useResetStaffCredential`) and the step-up token.
   */
  onConfirm: (reason: string) => Promise<void>;
  /** Disables the submit button while the parent mutation is in flight. */
  isPending?: boolean;
  /**
   * The parent mutation's failure, rendered inside the dialog. The parent
   * clears it (`mutation.reset()`) when the dialog closes.
   */
  errorMessage?: string;
}

export function ResetCredentialConfirmDialog({
  open,
  onOpenChange,
  staffName,
  staffEmail,
  onConfirm,
  isPending = false,
  errorMessage,
}: ResetCredentialConfirmDialogProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset transient state whenever the dialog closes so a re-open starts
  // from a clean slate and no prior staff member's reason leaks forward.
  useEffect(() => {
    if (!open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON_LENGTH;
  const showError = touched && !isValid;

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValid || isPending) return;
    await onConfirm(trimmed);
  };

  const handleCancel = () => {
    if (isPending) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Reset sign-in credential for ${staffName}`}
      size="md"
      isDismissable={!isPending}
      isKeyboardDismissDisabled={isPending}
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Resetting...' : 'Reset sign-in credential'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {errorMessage && (
          <Alert type="error" title="Credential reset failed">
            {errorMessage}
          </Alert>
        )}
        <p className="text-sm font-sans text-secondary">
          Resetting deactivates the current password credential for{' '}
          <span className="font-mono text-primary">{staffEmail}</span>. A
          single-use reset invitation is emailed to that address, and live
          sessions are revoked, so the staff member signs in again only
          after setting a new password.
        </p>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(value) => {
            setReason(value);
            if (!touched) setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          required
          rows={4}
          maxLength={500}
          showCharCount
          fullWidth
          helperText={`Minimum ${MIN_REASON_LENGTH} characters. Recorded on the staff member's audit trail.`}
          error={showError ? `Provide at least ${MIN_REASON_LENGTH} characters.` : undefined}
          placeholder="Explain the operational reason for resetting this credential."
        />
      </div>
    </Dialog>
  );
}

export default ResetCredentialConfirmDialog;
